import { AnimatePresence } from 'framer-motion';
import type { Delta, Range } from 'quill/core';
import Quill from 'quill/core';
import type { ReactNode } from 'react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { usePopper } from 'react-popper';
import * as styles from './ToolbarPopOver.module.scss';
import ZoomInAnimation from '../../../components/ZoomInAnimation';
import { isTouchDevice } from '../../../constants/os';
import ClickOutsideHandler from '../../../utils/ClickOutsideHandler';
import { READONLY_CHANGE_EVENT } from '../../constants';
import { useQuill } from '../../QuillContext';
import { useUIElementResizeListener } from '../../UIElementResizeContext';

const getToolbarBoundsForMultilineSelection = (quill: Quill, range: Range) => {
  // Only consider nearest line in tooltip bounds
  if (isTouchDevice) {
    const [, offset] = quill.getLine(range.index + range.length);
    return quill.getBounds(range.index + range.length - offset, offset);
  }
  const [line, offset] = quill.getLine(range.index);
  return line ? quill.getBounds(range.index, line.length() - offset - 1) : null;
};

const useToolbarOpen = (quill: Quill | null) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!quill) return undefined;
    if (quill.getSelection()) setIsOpen(true);

    const selectionListener = (range: Range) => {
      if (range) setIsOpen(true);
    };

    const textListener = (delta: Delta, _old: unknown, source: string) => {
      if (
        source === Quill.sources.USER &&
        delta.ops.some((op) => op.insert || op.delete)
      ) {
        setIsOpen(false);
      }
    };

    // When user clicks on the post mode switcher
    const readOnlyListener = () => {
      const range = quill.getSelection();
      if (range) setIsOpen(true);
    };

    quill.on(Quill.events.SELECTION_CHANGE, selectionListener);
    quill.on(Quill.events.TEXT_CHANGE, textListener);
    quill.on(READONLY_CHANGE_EVENT, readOnlyListener);
    return () => {
      quill.off(Quill.events.SELECTION_CHANGE, selectionListener);
      quill.off(Quill.events.TEXT_CHANGE, textListener);
      quill.off(READONLY_CHANGE_EVENT, readOnlyListener);
    };
  }, [quill]);

  return { isOpen, closeToolbar: () => setIsOpen(false) };
};

const useToolbarBounds = () => {
  const [bounds, setBounds] = useState<{
    left: number;
    width: number;
    top: number;
    height: number;
  } | null>(null);

  const {
    quill,
    selection: { range },
  } = useQuill();

  const calculateBounds = useCallback(() => {
    if (!quill || !range) return;

    const selectionBounds =
      quill.getLines(range).length > 1
        ? getToolbarBoundsForMultilineSelection(quill, range)
        : quill.getBounds(range);

    if (!selectionBounds) {
      return;
    }

    // Make sure the reference position is inside editor
    const left = Math.max(selectionBounds.left, 0);
    const right = Math.min(
      selectionBounds.right,
      quill.container.getBoundingClientRect().width,
    );

    setBounds({
      left,
      width: right - left,
      top: selectionBounds.top,
      height: selectionBounds.height,
    });
  }, [quill, range]);

  const addUIElementResizeListener = useUIElementResizeListener();

  useLayoutEffect(() => {
    calculateBounds();
    return addUIElementResizeListener(calculateBounds);
  }, [addUIElementResizeListener, calculateBounds]);

  return bounds;
};

interface ToolbarPopOverProps {
  children: (state: {
    lines: unknown[];
    range: Range;
    isInTitleLine: boolean;
    closeToolbar: () => void;
  }) => ReactNode;
  portal?: HTMLElement | null;
  showInTitleLine?: boolean;
}

const ToolbarPopOver = ({
  portal,
  children,
  showInTitleLine,
}: ToolbarPopOverProps) => {
  const {
    quill,
    selection: { range },
  } = useQuill();

  const [isInTitleLine, lines] = useMemo(() => {
    if (!quill || !range) return [false, null];
    const isTitleFocused = quill.isTitleFocused();
    if (!showInTitleLine && isTitleFocused) return [true, null];
    return [isTitleFocused, quill.getLines(range)];
  }, [quill, range, showInTitleLine]);

  const toolbarBounds = useToolbarBounds();

  const { isOpen, closeToolbar } = useToolbarOpen(quill);

  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [referenceElement, setReferenceElement] =
    useState<HTMLDivElement | null>(null);

  const {
    styles: { popper },
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    // For touch devices, putting the toolbar underneath to avoid overlapping
    // with the system context menu.
    placement: isTouchDevice ? 'bottom' : 'top',
    modifiers: [
      { name: 'preventOverflow', options: { padding: 10 } },
      {
        name: 'flip',
        options: { padding: 10 },
      },
      { name: 'offset', options: { offset: [0, isTouchDevice ? 16 : 10] } },
    ],
  });

  const updateRef = useRef<typeof update>(null);

  // `update` can be null when popper is not ready.
  updateRef.current = update;

  // reposition when selection bounds changed
  useEffect(() => {
    updateRef.current?.();
  }, [toolbarBounds]);

  // reposition when toolbar content resized
  useLayoutEffect(() => {
    if (!popperElement) return undefined;
    const resizeObserver = new ResizeObserver(() => {
      updateRef.current?.();
    });
    resizeObserver.observe(popperElement);
    return () => resizeObserver.disconnect();
  }, [popperElement]);

  const content =
    toolbarBounds &&
    isOpen &&
    range &&
    lines &&
    children({ lines, range, isInTitleLine, closeToolbar });

  const toolbar = (
    <ClickOutsideHandler
      data-no-print
      onClickOutside={(e) => {
        const clickedBlot =
          e.target instanceof Element && quill?.scroll.find(e.target);

        if (!clickedBlot) {
          closeToolbar();
        }
      }}
    >
      <div
        className={styles.toolbarContainer}
        ref={content ? setPopperElement : undefined}
        style={popper}
        {...attributes.popper}
      >
        <AnimatePresence>
          {!!content && (
            <ZoomInAnimation
              initial={{
                transformOrigin: 'bottom',
              }}
              className={styles.toolbar}
              role="toolbar"
            >
              {content}
            </ZoomInAnimation>
          )}
        </AnimatePresence>
      </div>
    </ClickOutsideHandler>
  );

  return (
    <>
      {!!content && toolbarBounds && (
        <div
          ref={setReferenceElement}
          className={styles.reference}
          style={toolbarBounds}
        />
      )}
      {portal ? createPortal(toolbar, portal) : toolbar}
    </>
  );
};

export default ToolbarPopOver;
